import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { CiHeart } from "react-icons/ci";
import { HiOutlineShoppingBag } from "react-icons/hi2";
import { FaArrowRight } from "react-icons/fa6";
import { CgMenu, CgCloseR } from "react-icons/cg";
import { IoSettingsOutline } from "react-icons/io5";
import { FiLogOut, FiUser } from "react-icons/fi";
import { allImagesPath, navigationData } from "../lib/constants/ConstantsData";
import { useAuthStore } from "../store/authStore";
import { useCartStore } from "../store/cartStore";

const Navbar = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { items, getTotalItems, getTotalPrice, fetchCart } = useCartStore();

  // Handle clicking outside of dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Disable scroll when mobile menu is open
  useEffect(() => {
    if (isShowModal) {
      // Disable scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Re-enable scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isShowModal]);

  const toAuth = () => {
    navigate("/auth/login");
  };

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate("/auth/login");
  };

  const toProfile = () => {
    const role = user?.role?.toLowerCase();
    if (role === 'admin' || role === 'sub-admin') {
      navigate("/dashboard/admin-home");
    } else {
      navigate("/dashboard/home");
    }
    setShowDropdown(false);
  };

  const toSettings = () => {
    navigate("/dashboard/profile");
    setShowDropdown(false);
  };

  // Format cart total price
  const cartTotalPrice = getTotalPrice().toFixed(2);

  // Function to handle cart click
  const handleCartClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default navigation

    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate("/auth/login");
      return;
    }

    try {
      setIsCartLoading(true);
      // Fetch latest cart data
      await fetchCart();
      // Then navigate to cart page
      navigate("/cart");
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsCartLoading(false);
    }
  };

  return (
    <nav className="w-full py-2 text-white ">
      {/* Desktop Navigation */}
      <div className="mx-auto flex items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="lg:w-[20%] ">
          <img
            className="h-[94px] w-[103px] object-contain"
            src={allImagesPath.logo}
            alt="Logo"
          />
        </Link>
        {/* Desktop Navigation Links */}
        <ul className=" xl:text-lg hidden lg:flex lg:items-center lg:justify-center lg:gap-5 lg:text-sm xl:flex xl:items-center xl:justify-center xl:gap-5 2xl:text-lg">
          {navigationData.map((item, index) => (
            <li
              className="cursor-pointer transition-all duration-300 hover:scale-105"
              key={index}
            >
              <NavLink
                to={item.link}
                className={({ isActive }) =>
                  `transition font-sfPro hover:text-gray-300 ${isActive ? "border-b border-white" : ""
                  }`
                }
              >
                {item.text}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Desktop Cart and Buttons */}
        <div className="hidden items-center justify-end gap-4 lg:flex lg:w-[25%] xl:w-[25%] xl:gap-6">

          {isAuthenticated && (
            <button onClick={handleCartClick} className="flex items-center gap-2 group relative">
              <div className="relative">
                {isCartLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                ) : (
                  <HiOutlineShoppingBag className="text-xl xl:text-2xl" />
                )}
                {getTotalItems() > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
                    {getTotalItems()}
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm lg:text-xs xl:text-xs 2xl:text-sm">
                  Shopping Cart
                </p>
                <p className="text-sm xl:text-base">${cartTotalPrice}</p>
              </div>
            </button>
          )}

          {isAuthenticated ? (
            <div className="relative inline-block text-left" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex h-[45px] items-center justify-center gap-2 rounded-full bg-white px-4 text-black transition-all duration-300"
                id="user-menu-button"
                aria-expanded={showDropdown}
                aria-haspopup="true"
              >
                <FiUser className="text-lg" />
                <span className="font-medium font-sfPro">{user?.username || "User"}</span>
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div
                  className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                >
                  <div className="py-1">
                    <button
                      onClick={toProfile}
                      className="flex w-full items-center gap-2 px-4 py-3 text-left font-sfPro text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      <FiUser className="text-gray-600" />
                      Dashboard
                    </button>
                    <button
                      onClick={toSettings}
                      className="flex w-full items-center gap-2 px-4 py-3 font-sfPro text-left text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      <IoSettingsOutline className="text-gray-600" />
                      Update Profile
                    </button>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-3 font-sfPro text-left text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      <FiLogOut className="text-gray-600" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <button
                onClick={toAuth}
                className="flex h-[45px] w-[150px] cursor-pointer items-center font-sfPro justify-center gap-2 rounded-full bg-white p-4 text-black transition-all duration-300 sm:h-[40px] lg:text-xs xl:text-base"
              >
                Get Started <FaArrowRight />
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button onClick={() => setIsShowModal(true)} className="lg:hidden">
          <CgMenu className="text-3xl" />
        </button>
      </div>

      {/* Mobile Navigation Modal */}
      
      <div
        className={` fixed inset-0 z-50 flex transform flex-col items-center justify-center bg-black bg-opacity-90 ${isShowModal ? "translate-x-0" : "translate-x-full"
          } transition-transform duration-300`}

          
      >
          <div onClick={() => navigate("/")} className=" flex items-center cursor-pointer justify-center   -mt-28">
                  <img
                    src="/images/home/Obscura_White_Logo2.png"
                    alt="Obscura Logo"
                    className=" "
                  />
                </div>
       
        {/* Close Button */}
        <button
          onClick={() => setIsShowModal(false)}
          className="absolute right-5 top-10 text-white"
        >
          <CgCloseR className="text-3xl" />
        </button>

        {/* Mobile Navigation Links */}
        <ul className="flex w-[60%] flex-col items-center justify-center gap-6 mt-8">
          {navigationData.map((item, index) => (
            <li key={index}>
              <Link
                className="transition hover:text-gray-300"
                to={item.link}
                onClick={() => setIsShowModal(false)}
              >
                {item.text}
              </Link>
            </li>
          ))}
        </ul>

        {/* Mobile Cart & Button */}
        <div className="mt-8 flex flex-col items-center space-y-6">
          {isAuthenticated ? (
            <>
              <div className="flex flex-col items-center gap-4">
                <span className="text-xl font-semibold">Hello, {user?.username || "User"}</span>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      toProfile();
                      setIsShowModal(false);
                    }}
                    className="flex items-center gap-2 text-white hover:text-gray-300"
                  >
                    <FiUser />
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      toSettings();
                      setIsShowModal(false);
                    }}
                    className="flex items-center gap-2 text-white hover:text-gray-300"
                  >
                    <IoSettingsOutline />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsShowModal(false);
                    }}
                    className="flex items-center gap-2 text-white hover:text-gray-300"
                  >
                    <FiLogOut />
                    Logout
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={(e) => {
                    handleCartClick(e);
                    setIsShowModal(false);
                  }}
                  className="flex items-center gap-2 text-white hover:text-gray-300"
                >
                  <HiOutlineShoppingBag />
                  Cart ({getTotalItems()})
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/">
                <CiHeart className="text-3xl" />
              </Link>

              <div className="flex items-center gap-4">
                <button
                  onClick={(e) => {
                    handleCartClick(e);
                    setIsShowModal(false);
                  }}
                  className="flex items-center gap-2 text-white hover:text-gray-300"
                >
                  <HiOutlineShoppingBag />
                  Cart ({getTotalItems()})
                </button>
              </div>

              <button
                onClick={() => {
                  toAuth();
                  setIsShowModal(false);
                }}
                className="flex h-[45px] w-[150px] cursor-pointer items-center justify-center gap-2 rounded-full bg-white p-4 text-black transition-all duration-300"
              >
                Get Started <FaArrowRight />
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
